import {useCallback, useEffect} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import {SWRConfig} from 'swr'

import {DevBanner} from "@components/DevBanner.tsx";
import {ThemeProvider} from '@components/theme-provider'
import {AgentProvider} from '@contexts/AgentContext'
import {EnvironmentProvider} from "@contexts/EnvironmentContext.tsx";
import {IntegrationProvider} from '@contexts/IntegrationContext'
import {UserProvider} from '@contexts/UserContext'
import {AgentLayout} from '@layouts/AgentLayout.tsx'
import {AgentLayoutEmbedded} from '@layouts/AgentLayoutEmbedded.tsx'
import {AuthenticatedLayout} from '@layouts/AuthenticatedLayout.tsx'
import {AccountPage} from "@pages/AccountPage.tsx";
import {AgentChatPage} from '@pages/AgentChatPage.tsx'
import {AgentsPage} from '@pages/AgentsPage'
import {ConnectedIntegration} from "@pages/ConnectedIntegration.tsx";
import {ConnectToIntegration} from "@pages/ConnectToIntegration.tsx";
import {LoginPage} from "@pages/LoginPage.tsx";
import {LogoutPage} from "@pages/LogoutPage.tsx";
import {ScratchPage} from '@pages/ScratchPage'
import {SettingsPage} from "@pages/SettingsPage.tsx";
import {WorkspacePage} from "@pages/WorkspacePage";

import type {ApiError} from '@lib/fetcher'

/** Max SWR error retries per hook instance. */
const MAX_ERROR_RETRIES = 3;
/** Non-retryable error kinds — stop immediately. */
const NO_RETRY_KINDS = new Set(["auth", "permission"]);

function App() {
    useEffect(() => {
        document.title = "Pecunio Agents";
    }, []);

    const onErrorRetry = useCallback(
        (error: ApiError, _key: string, _config: unknown, revalidate: (opts?: {retryCount: number}) => void, {retryCount}: {retryCount: number}) => {
            // Never retry auth or permission errors
            if (error?.kind && NO_RETRY_KINDS.has(error.kind)) return;
            // Hard cap on retries
            if (retryCount >= MAX_ERROR_RETRIES) return;
            // Exponential backoff: 2s, 4s, 8s
            const delay = Math.min(2000 * Math.pow(2, retryCount), 30000);
            setTimeout(() => revalidate({retryCount: retryCount + 1}), delay);
        },
        [],
    );

    return (
        <SWRConfig value={{onErrorRetry, dedupingInterval: 5000, revalidateOnFocus: false}}>
        <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            disableTransitionOnChange
        >
            <EnvironmentProvider>
                <UserProvider>
                    <IntegrationProvider>
                        <AgentProvider>
                        <DevBanner/>
                        <Routes>
                            <Route path="/login" element={<LoginPage/>}/>
                            <Route path="/signup" element={<LoginPage/>}/>
                            <Route path="/integrations/:slug/connect" element={<ConnectToIntegration/>}/>
                            <Route path="/integrations/:slug/connected" element={<ConnectedIntegration/>}/>
                            <Route path="/logout" element={<LogoutPage/>}/>
                            <Route path="/agents/:slug/embedded"
                                   element={<AgentLayoutEmbedded><AgentChatPage/></AgentLayoutEmbedded>}/>
                            <Route path="/agents/:slug"
                                   element={<AgentLayout><AgentChatPage/></AgentLayout>}/>
                            <Route path="/*" element={
                                <AuthenticatedLayout>
                                    <Routes>
                                        <Route path="/" element={<Navigate to="/dashboard" replace />}/>
                                        <Route path="/dashboard" element={<WorkspacePage initialTab="dashboard"/>}/>
                                        <Route path="/financialmodel" element={<WorkspacePage initialTab="financial"/>}/>
                                        <Route path="/alseasons" element={<WorkspacePage initialTab="alseasons"/>}/>
                                        <Route path="/agents" element={<AgentsPage/>}/>
                                        <Route path="/scratch" element={<ScratchPage/>}/>
                                        <Route path="/settings" element={<SettingsPage/>}/>
                                        <Route path="/account" element={<AccountPage/>}/>
                                    </Routes>
                                </AuthenticatedLayout>
                            }/>
                        </Routes>
                        </AgentProvider>
                    </IntegrationProvider>
                </UserProvider>
            </EnvironmentProvider>
        </ThemeProvider>
        </SWRConfig>
    )
}

export default App
